import { createContext, useContext, ReactNode } from "react";
import type { CustomerDataAdapter } from "./customer-adapter";

const CustomerDataAdapterContext = createContext<CustomerDataAdapter | null>(
  null,
);

interface CustomerDataAdapterProviderProps {
  adapter: CustomerDataAdapter;
  children: ReactNode;
}

/**
 * Provider that makes the customer portal's data adapter available to every
 * portal page. Mirrors `DataAdapterContext.tsx`'s shape for the staff
 * surface — a deliberately separate context because `CustomerDataAdapter`
 * is its own interface (see `customer-adapter.ts`'s header), not an
 * extension of the staff `DataAdapter`.
 */
export function CustomerDataAdapterProvider({
  adapter,
  children,
}: CustomerDataAdapterProviderProps) {
  return (
    <CustomerDataAdapterContext.Provider value={adapter}>
      {children}
    </CustomerDataAdapterContext.Provider>
  );
}

/** Hook to access the customer data adapter from any portal component. */
export function useCustomerDataAdapter(): CustomerDataAdapter {
  const adapter = useContext(CustomerDataAdapterContext);
  if (!adapter) {
    throw new Error(
      "useCustomerDataAdapter must be used within a CustomerDataAdapterProvider",
    );
  }
  return adapter;
}

export { CustomerDataAdapterContext };
